import { PDFDocument, PDFFont, grayscale, rgb } from 'pdf-lib';
import { PDF_PAGE_WIDTH } from '../../constants';

// Mirrors the backend's format_signer_date (apps/document/utils/pdf.py) so a
// linked date's client-baked stamp matches what the DocuSign/template path
// would produce for the same date_format property.
export function formatSignerDate(date: Date, fmtKey?: string): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  const month = date.toLocaleString('en-US', { month: 'long' });
  if (fmtKey === 'intl_short') {
    return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}`;
  }
  if (fmtKey === 'us_long') {
    return `${month} ${date.getDate()}, ${date.getFullYear()}`;
  }
  if (fmtKey === 'intl_long') {
    return `${date.getDate()} ${month} ${date.getFullYear()}`;
  }
  return `${pad(date.getMonth() + 1)}/${pad(date.getDate())}/${date.getFullYear()}`;
}

export function sortByPosition(a: PDF.PDFField, b: PDF.PDFField) {
  if (a.page === b.page) {
    return a.position.y - b.position.y || a.position.x - b.position.x;
  }
  return a.page - b.page;
}

function getFieldTypographyStyles(field: PDF.PDFField) {
  const {
    font_size: fontSize = 14,
    font_color: fontColor = '000000',
    font_family: fontFamily = 'Helvetica',
    font_weight: fontWeight = 400
  } = field.styles ?? {};

  const hex = fontColor.replace('#', '');
  const color = rgb(
    parseInt(hex.slice(0, 2), 16) / 255,
    parseInt(hex.slice(2, 4), 16) / 255,
    parseInt(hex.slice(4, 6), 16) / 255
  );

  return { fontSize, fontFamily, fontWeight, color };
}

export function drawText(
  field: PDF.PDFField,
  pdf: PDFDocument,
  fonts: Record<string, PDFFont>
) {
  if (!field.value) return;

  const { fontSize, fontFamily, fontWeight, color } =
    getFieldTypographyStyles(field);

  const inputWidth = field.dimensions.width;
  const inputHeight = field.dimensions.height;
  const lineHeight = fontSize * 1.2;
  const isTextArea = inputHeight > Math.ceil(2 * lineHeight);
  const inputOffset = inputHeight + (inputHeight - fontSize) / 2;

  const page = pdf.getPage(field.page);
  const pageHeight = page.getHeight();
  const pageWidth = page.getWidth();
  const pageScale = pageWidth / PDF_PAGE_WIDTH;

  const embeddedFont = fonts[`${fontFamily}-${fontWeight}`] ?? undefined;

  page.drawText(field.value, {
    x: field.position.x * pageScale,
    y:
      pageHeight -
      field.position.y * pageScale -
      (isTextArea ? fontSize * pageScale : (inputOffset / 2) * pageScale),
    lineHeight: isTextArea ? lineHeight * pageScale : inputHeight * pageScale,
    size: fontSize * pageScale,
    maxWidth: inputWidth * pageScale,
    color,
    font: embeddedFont,
    wordBreaks: ['']
  });
}

export function drawCombobox(
  field: PDF.PDFField,
  pdf: PDFDocument,
  fonts: Record<string, PDFFont>
) {
  if (!field.value) return;

  const { fontSize, fontFamily, fontWeight, color } =
    getFieldTypographyStyles(field);

  const inputHeight = field.dimensions.height;
  const inputWidth = field.dimensions.width;
  const inputOffset = inputHeight + (inputHeight - fontSize) / 2;

  const page = pdf.getPage(field.page);
  const pageHeight = page.getHeight();
  const pageWidth = page.getWidth();
  const pageScale = pageWidth / PDF_PAGE_WIDTH;

  const embeddedFont = fonts[`${fontFamily}-${fontWeight}`] ?? undefined;

  page.drawText(field.value, {
    x: field.position.x * pageScale,
    y:
      pageHeight - field.position.y * pageScale - (inputOffset / 2) * pageScale,
    lineHeight: inputHeight * pageScale,
    size: fontSize * pageScale,
    maxWidth: inputWidth * pageScale,
    color,
    font: embeddedFont,
    wordBreaks: ['']
  });
}

export async function drawSignature(field: PDF.PDFField, pdf: PDFDocument) {
  if (!field.value) return;

  const imageData = await (field.value as File).arrayBuffer();
  const image = await pdf.embedPng(imageData);
  const page = pdf.getPage(field.page);
  const pageHeight = page.getHeight();
  const pageWidth = page.getWidth();
  const pageScale = pageWidth / PDF_PAGE_WIDTH;

  const boxWidth = field.dimensions.width * pageScale;
  const boxHeight = field.dimensions.height * pageScale;

  // object fit contain
  const naturalDims = image.scale(1);
  const scale = Math.min(
    boxWidth / naturalDims.width,
    boxHeight / naturalDims.height
  );
  const imgWidth = naturalDims.width * scale;
  const imgHeight = naturalDims.height * scale;

  page.drawImage(image, {
    x: field.position.x * pageScale,
    y: pageHeight - field.position.y * pageScale - imgHeight,
    width: imgWidth,
    height: imgHeight
  });
}

const CHECK_SVG = {
  path: 'M20 6 9 17l-5-5',
  size: 24,
  strokeWidth: 4,
  strokeColor: grayscale(1)
};

const SQUARE_SVG = {
  path: 'M 0 3 C 0 1.3432 1.3432 0 3 0 H 15 C 16.6569 0 18 1.3432 18 3 V 15.3149 C 18 16.8435 16.8506 18.1277 15.3313 18.2965 L 2.6687 18.2965 C 1.1494 18.1277 0 16.8435 0 15.3149 V 3 Z',
  size: 18
};

export function drawCheckbox(field: PDF.PDFField, pdf: PDFDocument) {
  const page = pdf.getPage(field.page);
  const pageHeight = page.getHeight();
  const pageWidth = page.getWidth();
  const pageScale = pageWidth / PDF_PAGE_WIDTH;
  const fieldWidth = field.dimensions.width;

  const isRadio = field.type === 'radio';
  const isChecked = field.value === true;

  if (isChecked) {
    if (isRadio) {
      const circleRadius = fieldWidth / 2;
      page.drawCircle({
        x: field.position.x * pageScale + circleRadius,
        y: pageHeight - field.position.y * pageScale - circleRadius,
        size: circleRadius,
        color: rgb(0.886, 0.384, 0.431),
        borderWidth: 1,
        borderColor: rgb(0.886, 0.384, 0.431)
      });
      page.drawSvgPath(CHECK_SVG.path, {
        x: field.position.x * pageScale,
        y: pageHeight - field.position.y * pageScale,
        borderColor: grayscale(1),
        borderWidth: CHECK_SVG.strokeWidth,
        scale: fieldWidth / CHECK_SVG.size
      });
    } else {
      page.drawSvgPath(SQUARE_SVG.path, {
        x: field.position.x * pageScale,
        y: pageHeight - field.position.y * pageScale,
        color: rgb(0.886, 0.384, 0.431),
        borderWidth: 1,
        borderColor: rgb(0.886, 0.384, 0.431),
        scale: fieldWidth / SQUARE_SVG.size
      });
      page.drawSvgPath(CHECK_SVG.path, {
        x: field.position.x * pageScale,
        y: pageHeight - field.position.y * pageScale,
        borderColor: grayscale(1),
        borderWidth: CHECK_SVG.strokeWidth,
        scale: fieldWidth / CHECK_SVG.size
      });
    }
  } else {
    if (isRadio) {
      const circleRadius = fieldWidth / 2;
      page.drawCircle({
        x: field.position.x * pageScale + circleRadius,
        y: pageHeight - field.position.y * pageScale - circleRadius,
        size: circleRadius,
        color: grayscale(0.933),
        borderWidth: 1,
        borderColor: grayscale(0.733)
      });
    } else {
      page.drawSvgPath(SQUARE_SVG.path, {
        x: field.position.x * pageScale,
        y: pageHeight - field.position.y * pageScale,
        color: grayscale(0.933),
        borderWidth: 1,
        borderColor: grayscale(0.733),
        scale: fieldWidth / SQUARE_SVG.size
      });
    }
  }
}
